
import { FileText, Download, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UploadDocumentForm } from "./UploadDocumentForm";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { deleteDocument } from "./document-utils";
import { DocumentDisplayNameEdit } from "./DocumentDisplayNameEdit";

interface Section {
  id: string;
  name: string;
  position: number;
}

interface Document {
  id: string;
  name: string;
  display_name?: string;
  file_path: string;
  section_id: string;
  created_at: string;
  size: number;
  position: number;
  content_type: string;
  created_by: string | null;
  creator_email: string | null;
  creator_name: string | null;
  organization_id: string;
}

interface DocumentGridViewProps {
  selectedSection: Section;
  documents: Document[];
  onDocumentDeleted: (documentId: string) => void;
  onDocumentUploaded: () => void;
  onDocumentUpdated?: (docId: string, newDisplayName: string) => void;
  isMandatorySection: boolean;
  onDownload: (doc: Document) => void;
  isLoadingDocuments: boolean;
  organizationId: string;
}

export const DocumentGridView = ({
  selectedSection,
  documents,
  onDocumentDeleted,
  onDocumentUploaded,
  onDocumentUpdated,
  isMandatorySection,
  onDownload,
  isLoadingDocuments,
  organizationId
}: DocumentGridViewProps) => {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (doc: Document) => {
    setDocumentToDelete(doc);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteDocument(documentToDelete, onDocumentDeleted);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return `Updated: ${formatDistanceToNow(new Date(dateString))} ago`;
    } catch {
      return "Updated: Unknown";
    }
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      {/* Upload Section */}
      <div className="mb-8 border-b border-[#dce0e5] pb-6">
        <Button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="mb-4 bg-[#2F80ED] hover:bg-[#2F80ED]/90"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
        
        {showUploadForm && (
          <div className="bg-[#f0f2f4] p-4 rounded-lg">
            <UploadDocumentForm
              sectionId={selectedSection.id}
              onDocumentUploaded={() => {
                onDocumentUploaded();
                setShowUploadForm(false);
              }}
              organizationId={organizationId}
            />
          </div>
        )}
      </div>

      {/* Documents Grid */}
      {isLoadingDocuments ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F80ED]"></div>
          <span className="ml-2 text-[#637588]">Loading documents...</span>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-[#637588] mx-auto mb-4" />
          <p className="text-[#637588] mb-2">No documents found</p>
          <p className="text-sm text-[#637588]/60">Upload your first document using the button above</p>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-6">
          {documents.map((doc) => (
            <div key={doc.id} className="flex flex-col">
              <div className="relative group mb-3">
                <div 
                  className="w-full bg-[#f0f2f4] h-36 rounded-lg flex items-center justify-center cursor-pointer hover:bg-[#e6eef5] transition-colors border border-[#dce0e5]"
                  onClick={() => onDownload(doc)}
                >
                  <FileText className="h-12 w-12 text-[#2F80ED]" />
                </div>
                
                {/* Action buttons overlay */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(doc);
                    }}
                    className="h-8 w-8 bg-white/95 hover:bg-white border-[#dce0e5] shadow-sm"
                    title="Download"
                  >
                    <Download className="h-4 w-4 text-[#2F80ED]" />
                  </Button>
                  
                  {!isMandatorySection && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(doc);
                      }}
                      className="h-8 w-8 bg-white/95 hover:bg-white border-[#dce0e5] shadow-sm"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-[#ea384c]" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div>
                {onDocumentUpdated ? (
                  <div className="mb-1">
                    <DocumentDisplayNameEdit 
                      doc={doc} 
                      onDocumentUpdated={onDocumentUpdated}
                    />
                  </div>
                ) : (
                  <p className="text-[#111418] text-sm font-medium leading-normal truncate mb-1" title={doc.display_name || doc.name}>
                    {doc.display_name || doc.name}
                  </p>
                )}
                {doc.display_name && (
                  <p className="text-[#637588] text-xs font-normal leading-normal truncate mb-1" title={doc.name}>
                    File: {doc.name}
                  </p>
                )}
                <p className="text-[#637588] text-xs font-normal leading-normal">
                  {formatDate(doc.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Document"
        description={`Are you sure you want to delete "${documentToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
